import { useState, useCallback, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Deployment from "../services/deployment";
import { useGetDomain, useHasPermission, useGetUser } from './Hooks';
import Utils from "../utils/utils";
import PubSub from "../services/PubSub";

const useNavbar = (() => {
    const userPermissions = useHasPermission({
        domain: Utils.getDomain(),
        environment: null,
        deployment: null,
        type: "user"
    });
    const environmentPermissions = useHasPermission({
        domain: Utils.getDomain(),
        environment: null,
        deployment: null,
        type: "environment"
    });

    const domainPermissions = useHasPermission({
        domain: Utils.getDomain(),
        environment: null,
        deployment: null,
        type: "domain"
    });

    const auditTrailPermissions = useHasPermission({
        domain: Utils.getDomain(),
        environment: null,
        deployment: null,
        type: "auditTrail"
    });
    const sessionLogsPermissions = useHasPermission({
        domain: Utils.getDomain(),
        environment: null,
        deployment: null,
        type: "sessionLogs"
    });
    const { id } = useParams();
    const [translate] = useTranslation();
    const history = useHistory();

    const notificationsPermissions = useHasPermission({
        domain: Utils.getDomain(),
        environment: null,
        deployment: id,
        type: "notification"
    });

    const domainManagementTabs = useCallback(() => [
        {
            name: translate("OTM_USERS"),
            id: "user",
            visible: userPermissions.read
        },
        {
            name: translate("OTM_ENVIRONMENTS"),
            id: "environment",
            visible: environmentPermissions.read
        },
        {
            name: translate("OTM_DOMAIN_SETTINGS"),
            id: "domain-settings",
            visible: domainPermissions.read
        },
        {
            name: translate("OTM_AUDIT_TRAIL"),
            id: "audit-trail",
            visible: auditTrailPermissions.read
        },
        {
            name: translate("OTM_SITE_MONITORING"),
            id: "site-monitoring",
            visible: true
        }
    ], [translate, domainPermissions.read, auditTrailPermissions.read, environmentPermissions.read, userPermissions.read]);

    const isRootDomain = Utils.isRootDomain();
    const [domain] = useGetDomain();
    const [drillDownObjects, setDrillDownObjects] = useState([]);
    const [deployments, setDeployments] = useState([]);
    const [cursors, setCursors] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [deploymentParams, setDeploymentParams] = useState({
        pageIncrement: 0
    });
    const [selectedTab, setSelectedTab] = useState("domain-management");
    const [user] = useGetUser();
    const [onPremDeployment, setOnPremDeployment] = useState(false);
    const [isFormsDeployment, setIsFormsDeployment] = useState(false);

    const populateDrillDownObjects = useCallback(() => {
        setDrillDownObjects(() => {
            if (isRootDomain) {
                return [
                    {
                        name: translate("OTM_USERS"),
                        id: "user",
                        visible: user.appAdmin
                    },
                    {
                        name: translate("OTM_PRODUCTS"),
                        id: "products",
                        visible: domainPermissions.read
                    },
                    {
                        name: translate("OTM_ENVIRONMENT_TEMPLATES"),
                        id: "environment-templates",
                        visible: domainPermissions.read
                    },
                    {
                        name: translate("OTM_LOGS"),
                        id: "domain-logs",
                        visible: true,
                    },
                    {
                        name: translate("OTM_DOMAINS"),
                        id: "domain",
                        visible: domainPermissions.read
                    },
                    {
                        name: translate("OTM_AUDIT_TRAIL"),
                        id: "audit-trail",
                        visible: auditTrailPermissions.read
                    }
                ]
            }

            if (selectedTab === "domain-management") {
                return domainManagementTabs();
            }

            return [
                {
                    name: translate("OTM_DEPLOYMENT_MANAGEMENT"),
                    id: "deployment-management",
                    visible: true
                },
                {
                    name: translate("OTM_RUNTIME_CONFIG"),
                    id: "runtime-config",
                    visible: onPremDeployment === false
                },
                {
                    name: translate("OTM_BUILD_TIME_CONFIG"),
                    id: "build-time-config",
                    visible: true
                },
                {
                    name: translate("OTM_FORMS"),
                    id: "forms-config",
                    visible: isFormsDeployment === true
                },
                {
                    name: translate("OTM_NOTIFICATIONS"),
                    id: "notifications",
                    visible: notificationsPermissions.read,
                },
                {
                    name: translate("OTM_SUPPORT_LOGS"),
                    id: "support-logs",
                    visible: sessionLogsPermissions.read
                },
            ];
        });
    }, [user.appAdmin, domainManagementTabs, onPremDeployment, isFormsDeployment, domainPermissions, auditTrailPermissions, sessionLogsPermissions,
        setDrillDownObjects, translate, selectedTab, isRootDomain, notificationsPermissions.read]);

    const getAllDeployments = useCallback(async (params) => {
        setDeployments([]);
        const response = await Deployment.getAllDeploymentsInDomain(
            params.domain,
            null,
            params.firstCursor,
            params.lastCursor,
            params.pageIncrement < 0,
            params.getLatest);

        if (response && !response.error) {
            setDeployments(response.data);
            setCurrentPage(page => {
                const result = params.getLatest ? Math.ceil(response.paging.count / response.paging.pageSize) : page + params.pageIncrement;
                setHasNextPage(response.paging.count > result * response.paging.pageSize);
                return result;
            });
            setCursors({
                firstCursor: response.paging.firstCursor,
                lastCursor: response.paging.lastCursor
            });

            if (Utils.isFormsUser() === true && response.data.length > 0) {
                history.push(`/forms-config/${response.data[0]._id}`)
            }
        }

        setDeploymentParams({});
    }, [setDeployments, history]);

    const getDeployment = useCallback(async (deploymentId) => {
        const response = await Deployment.getDeployment(deploymentId);

        if (response && !response.error) {
            setOnPremDeployment(response.data.productVersion?.onPrem);
            setIsFormsDeployment(response.data.productVersion?.isForms);
        }
    }, []);

    useEffect(() => {
        populateDrillDownObjects();
    }, [populateDrillDownObjects]);

    useEffect(() => {
        if (domain && deploymentParams.pageIncrement !== undefined) {
            getAllDeployments({ ...deploymentParams, domain: domain._id });
        }

        const id = PubSub.subscribe("refresh-environment-deployments", () => {
            setCursors({});
            setCurrentPage(1);
            setHasNextPage(false);
            setDeploymentParams({ pageIncrement: 0 });
        });

        return () => {
            PubSub.unsubscribe("refresh-environment-deployments", id);
        };
    }, [getAllDeployments, domain, deploymentParams]);

    useEffect(() => {
        if (deployments && deployments.length > 0) {
            deployments.forEach((deployment) => {
                if (deployment._id === id) {
                    setSelectedTab(deployment);
                    getDeployment(id);
                }
            })
        }
    }, [deployments, setSelectedTab, getDeployment, id]);

    const previous = useCallback(() => {
        setDeploymentParams({
            pageIncrement: -1,
            firstCursor: cursors.firstCursor,
            lastCursor: cursors.lastCursor
        });
    }, [cursors.firstCursor, cursors.lastCursor]);

    const next = useCallback(() => {
        setDeploymentParams({
            pageIncrement: 1,
            firstCursor: cursors.firstCursor,
            lastCursor: cursors.lastCursor
        });
    }, [cursors.firstCursor, cursors.lastCursor]);

    const handleDeploymentClick = useCallback((deployment) => {
        setSelectedTab(deployment);
        if (Utils.isFormsUser() === true) {
            history.push(`/forms-config/${deployment._id}`)
        }
        else {
            history.push(`/deployment-management/${deployment._id}`);
        }
    }, [history]);

    const handleDrillDownClick = useCallback((url, deploymentId) => {
        history.push(`/${url}${deploymentId ? '/' + deploymentId : ''}`);
    }, [history]);

    const navigateToLandingPage = useCallback(() => {
        for (var i = 0; i < domainManagementTabs().length; i++) {
            if (domainManagementTabs()[i].visible) {
                handleDrillDownClick(domainManagementTabs()[i].id);
                break;
            }
        }
    }, [domainManagementTabs, handleDrillDownClick]);

    const handleDomainManagementClick = useCallback(() => {
        setSelectedTab("domain-management");
        navigateToLandingPage();
    }, [navigateToLandingPage]);

    return {
        drillDownObjects,
        deployments,
        selectedTab,
        next,
        previous,
        currentPage,
        hasNextPage,
        isRootDomain,
        handleDomainManagementClick,
        handleDeploymentClick,
        handleDrillDownClick,
        navigateToLandingPage,
    }
});

export default useNavbar;